import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FloatingCTA from "@/components/feature/FloatingCTA";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import SEO from "@/components/feature/SEO";
import { organizationSchema, webpageSchema, breadcrumbSchema, siteUrl } from "@/lib/schemas";
import { Target, Users, Award, TrendingUp, Heart, Shield, ArrowRight, Star, MapPin } from "lucide-react";

const values = [
  { icon: Heart, title: "Customer-First", desc: "Every decision we make starts with one question: what's best for the customer? No insurer partnerships influence our recommendations." },
  { icon: Shield, title: "Transparency", desc: "We disclose commissions, explain exclusions upfront, and never hide the fine print. Honest advice is our only product." },
  { icon: Award, title: "Expertise", desc: "Our advisors are IRDAI-certified with 5+ years average experience. They understand products deeply — not just how to sell them." },
  { icon: TrendingUp, title: "Results", desc: "50,000+ customers served, 8,500+ claims assisted, 97.8% claim success rate. We measure success by outcomes, not sales." },
];

const team = [
  { name: "Rahul Mehta", role: "Founder & CEO", exp: "15+ years", focus: "Insurance Strategy", initial: "RM" },
  { name: "Priya Sharma", role: "Head of Advisory", exp: "12+ years", focus: "Health & Term Insurance", initial: "PS" },
  { name: "Amit Patel", role: "Claims Director", exp: "10+ years", focus: "Claims & Settlements", initial: "AP" },
  { name: "Neha Gupta", role: "Investment Lead", exp: "8+ years", focus: "Mutual Funds & SIP", initial: "NG" },
];

const stats = [
  { value: "50K+", label: "Happy Customers" },
  { value: "200+", label: "Certified Advisors" },
  { value: "8.5K+", label: "Claims Assisted" },
  { value: "97.8%", label: "Claim Success Rate" },
  { value: "25+", label: "Insurer Partners" },
  { value: "150+", label: "Cities Covered" },
];

export default function AboutPage() {
  const baseUrl = siteUrl;
  const pageUrl = `${baseUrl}/about`;

  const schemas = [
    webpageSchema(
      "About ExpertAdvise.in | India's Most Trusted Insurance Advisory",
      "Learn about ExpertAdvise.in's mission, team, and values. 200+ certified advisors, 50,000+ customers, 97.8% claim success rate. Expert insurance & financial advisory since 2018.",
      pageUrl
    ),
    organizationSchema(),
    breadcrumbSchema([
      { name: "Home", url: baseUrl },
      { name: "About Us", url: pageUrl },
    ]),
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="About ExpertAdvise.in | India's Most Trusted Insurance Advisory"
        description="Learn about ExpertAdvise.in's mission, team, and values. 200+ certified advisors, 50,000+ customers, 97.8% claim success rate. Expert insurance & financial advisory since 2018."
        keywords="insurance advisory India, ExpertAdvise team, about insurance advisor, financial advisory company, insurance consultant near me"
        canonical={pageUrl}
        ogUrl={pageUrl}
        schemas={schemas}
      />
      <Navbar />

      <section className="pt-24 md:pt-32 pb-10 md:pb-14 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <Breadcrumbs
              items={[
                { name: "Home", href: "/" },
                { name: "About Us" },
              ]}
              className="justify-center mb-4"
            />
            <span className="inline-block bg-primary-50 text-primary-700 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3">About Us</span>
            <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4">India's Most Trusted Insurance Advisory</h1>
            <p className="text-slate-600 text-base md:text-lg leading-relaxed">
              ExpertAdvise.in was founded with a simple mission: make insurance simple, transparent, and accessible to every Indian. 
              We don't sell policies — we solve problems. Our advisory-first approach ensures you get the right coverage, not the most profitable one.
            </p>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-primary-600 mb-1">{s.value}</div>
                <div className="text-xs md:text-sm text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">Our Story</h2>
              <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">
                <p>
                  ExpertAdvise.in started in 2018 when our founder, Rahul Mehta, witnessed his own father struggle with a rejected health insurance claim. 
                  Despite paying premiums for years, the family was left helpless against a wall of paperwork, jargon, and indifference.
                </p>
                <p>
                  That experience sparked a mission: to create an advisory platform that puts the customer first. Not the insurer. Not the commission. The customer.
                </p>
                <p>
                  Today, ExpertAdvise.in is a team of 200+ certified advisors serving 50,000+ customers across 150+ Indian cities. 
                  We have assisted in over 8,500 claims with a 97.8% success rate — because we stay with our customers long after the policy is sold.
                </p>
                <p>
                  We are not an insurance company. We are not a broker driven by targets. We are advisors — and we take that word seriously.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 md:p-10">
              <h3 className="text-lg font-bold text-slate-900 mb-5">Why We Are Different</h3>
              <div className="space-y-4">
                {[
                  "No insurer exclusivity — we compare 25+ insurers",
                  "Advisory-first, sales-second approach",
                  "Lifetime claims support — not just pre-sale",
                  "Transparent commission disclosure",
                  "Customized plans, not off-the-shelf recommendations",
                  "Dedicated relationship manager for every client",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Star className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Our Core Values</h2>
            <p className="text-slate-600">The principles that guide every interaction, recommendation, and decision we make.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-primary-50 flex items-center justify-center mb-4"><v.icon className="w-5 h-5 text-primary-600" /></div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{v.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Meet Our Leadership</h2>
            <p className="text-slate-600">Experienced professionals dedicated to your financial security.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            {team.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 text-center hover:shadow-lg transition-all duration-300">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white flex items-center justify-center text-lg font-bold mx-auto mb-4">
                  {t.initial}
                </div>
                <div className="font-bold text-slate-900">{t.name}</div>
                <div className="text-sm text-primary-600 font-medium mb-1">{t.role}</div>
                <div className="text-xs text-slate-500">{t.exp} experience</div>
                <div className="text-xs text-slate-400 mt-1">{t.focus}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-primary-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">Join 50,000+ Happy Customers</h2>
          <p className="text-slate-600 mb-6">Experience the ExpertAdvise difference. No pressure, no jargon — just honest, expert guidance.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/book-appointment?source=About%20Page" className="bg-cta-600 hover:bg-cta-700 text-white font-semibold px-6 py-3 rounded-full transition-colors whitespace-nowrap flex items-center gap-2">
              Book Free Consultation<ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/compare-plans" className="bg-white hover:bg-slate-50 text-slate-700 font-semibold px-6 py-3 rounded-full border border-slate-200 transition-colors whitespace-nowrap">
              Compare Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}